import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import moment from "moment";
import { fetchData } from "../screens/StudentData/FetchData";
import { applyDefaultSelection } from "../screens/StudentData/utils/defaultSelections";
import { useDeepCompareMemo } from "use-deep-compare";
import { useWellness } from "./WellnessContext";
import { useBootstrap } from "@/features/bootstrap";
import { barEmotionOrder } from "../screens/Dashboards/utils";

export const StudentDataContext = createContext();

export const StudentDataProvider = ({ children }) => {
  const { data: user } = useBootstrap();
  const { getAllRaw, getByUserId, loadedPeriods, loadingPeriods } =
    useWellness();
  const [state, setState] = useState({
    chosenSchool: "Choose School",
    chosenDistrict: "Choose District",
    chosenClass: "Choose Class",
    chosenStudent: "Choose Student",
    searchData: "",
    selectedGroupType: null,
    districts: {},
    chosenTime: "month",
    chosenTimeCompare: "month",
    allWellnessPoints: [],
    counter: 0,
    actionsCounter: 0,
    compareActionsCounter: 0,
    participationPercentChange: 0,
    studentData: {},
    multiEmotion: {
      status: true,
      emotions: ["sad", "lonely", "stressed", "angry", "scared"],
    },
    threeImpactfulActions: {},
    filterAmountCurrent: moment.duration(30, "days"),
    filterAmountCompare: moment.duration(30, "days"),
    feeling: "select",
    wellnessList: [],
    loading: true,
    counts: [
      { name: "sad", current: 0, compare: 0 },
      { name: "tired", current: 0, compare: 0 },
      { name: "lonely", current: 0, compare: 0 },
      { name: "happy", current: 0, compare: 0 },
      { name: "grateful", current: 0, compare: 0 },
      { name: "calm", current: 0, compare: 0 },
      { name: "stressed", current: 0, compare: 0 },
      { name: "scared", current: 0, compare: 0 },
      { name: "angry", current: 0, compare: 0 },
    ],
    actionCounts: [
      { name: "Smile big", current: 0, compare: 0 },
      { name: "Stretch", current: 0, compare: 0 },
      { name: "Be mindful. Be where my feet are", current: 0, compare: 0 },
      { name: "Help Others", current: 0, compare: 0 },
      {
        name: "Think of 3 things that I am grateful for",
        current: 0,
        compare: 0,
      },
      { name: "Give someone a compliment", current: 0, compare: 0 },
      { name: "Give someone a high five", current: 0, compare: 0 },
      { name: "Write in my journal", current: 0, compare: 0 },
      { name: "Create art", current: 0, compare: 0 },
      { name: "Imagine positive things in the future", current: 0, compare: 0 },
      { name: "Laugh at something funny", current: 0, compare: 0 },
      { name: "Remember that difficult times pass", current: 0, compare: 0 },
      {
        name: "Remind myself of two good things in my life",
        current: 0,
        compare: 0,
      },
      { name: "Tell myself something nice", current: 0, compare: 0 },
      { name: "Get some sun in my eyes", current: 0, compare: 0 },
      {
        name: "Count to ten taking a deep breath each time",
        current: 0,
        compare: 0,
      },
      { name: "Imagine a peaceful place", current: 0, compare: 0 },
      { name: "Listen to music or read", current: 0, compare: 0 },
      { name: "Politely move away from a distraction", current: 0, compare: 0 },
      { name: "Squeeze my fist", current: 0, compare: 0 },
      { name: "Use my growth mindset", current: 0, compare: 0 },
    ],
    allStudents: [],
    activeComponent: 0,
    studentEmailToNameMap: {},
    filteredDataPoints: [],
    lineGraphSourceData: [],
    lineGraphDerivedData: null,
    lineGraphSelectedEmotions: [...barEmotionOrder],
    chosenEmotion: "all emotions",
    chosenColor: "#C8FADD",
    studentEmotionData: {},
    chosenPercentage: 0,
    chosenPercentageRange: { min: 0, max: 100 },
    bigFiveFilteredData: [],
    selectedTimes: {
      "All Day": true,
      "Early Morning": false,
      Morning: false,
      Afternoon: false,
      Evening: false,
    },
    selectedSubOptions: {},
    helpfulFilter: "All",
    wellnessPopupOpen: false,
    currentStudents: [],
    chosenAlertType: "",
    chosenAlertStatus: "all",
    filteredAlerts: [],
    alerts: [],
    wellnessLoadedPeriods: loadedPeriods,
    wellnessloadingPeriods: loadingPeriods,
    nonConsented: [],
  });
  const stateRef = useRef(state);
  const [dataVersion, setDataVersion] = useState(0);

  const wellness = useMemo(
    () => ({ raw: getAllRaw(), byUserId: getByUserId() }),
    [getAllRaw, getByUserId],
  );
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const updateState = useCallback((updatedState) => {
    setState((prevState) => ({
      ...prevState,
      ...updatedState,
    }));
  }, []);

  const loadStudentData = useCallback(
    async (user, anonamizeData = false) => {
      updateState({ loading: true });
      const currentState = stateRef.current;
      try {
        const data = await fetchData(
          currentState,
          user,
          wellness,
          anonamizeData,
        );

        updateState({ ...data, loading: false });
        setDataVersion((v) => v + 1);
      } catch (error) {
        updateState({ loading: false, error: "Failed to fetch data" });
      }
    },
    [updateState, wellness],
  );

  useEffect(() => {
    loadStudentData(user);
  }, [user, loadStudentData]);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      wellnessLoadedPeriods: loadedPeriods,
      wellnessloadingPeriods: loadingPeriods,
    }));
  }, [loadedPeriods, loadingPeriods]);

  // One-time post-load default selection
  useEffect(() => {
    const currentState = stateRef.current;
    if (
      currentState.loading ||
      !currentState.districts ||
      Object.keys(currentState.districts).length === 0 ||
      !currentState.allStudents ||
      currentState.allStudents.length === 0
    ) {
      return;
    }
    const processedData = applyDefaultSelection(currentState, user);
    if (processedData) {
      updateState(processedData);
    }
  }, [dataVersion, updateState, user, wellness]);

  const studentDataContext = useDeepCompareMemo(
    () => ({ state, updateState, loadStudentData }),
    [state, updateState, loadStudentData],
  );

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      nonConsented: user?.nonConsented || [],
    }));
  }, [user]);

  return (
    <StudentDataContext.Provider value={studentDataContext}>
      {children}
    </StudentDataContext.Provider>
  );
};
